import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@xing-com/crate-core-router';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { AI_SEARCH_AUTOFOCUS_PARAM } from '@xing-com/crate-jobs-constants';
import {
  FindJobsTipBox,
  FindJobsTipBoxProps,
  Promotions,
} from '@xing-com/crate-jobs-domain-search-components-search-box';
import { useAnimatedPlaceholder } from '@xing-com/crate-jobs-domain-search-conversational-search-bar';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { IconAIFilled } from '@xing-com/icons';
import { mediaConfined, mediaSmallOrTiny } from '@xing-com/layout-tokens';
import { InputBar } from '@xing-com/text-field';
import { LightMode } from '@xing-com/theme-mode-switcher';
import { scale080, scale090, spaceL } from '@xing-com/tokens';

type TipBoxState = 'hidden' | 'input' | 'promotions';
type Props = {
  withPromotions?: boolean;
  tipBoxState: TipBoxState;
  isStartPage?: boolean;
  onClose: () => void;
  onClickNext: (state: Exclude<TipBoxState, 'hidden'>) => () => void;
};

export const FakeInput: React.FC<Props> = ({
  withPromotions,
  tipBoxState,
  isStartPage,
  onClose,
  onClickNext,
}) => {
  const { formatMessage } = useIntl();
  const isSmallScreen = useMatchMedia(mediaSmallOrTiny);

  const inputRef = useRef(null);
  const runPlaceholderAnimation = useAnimatedPlaceholder(inputRef);

  useEffect(() => {
    runPlaceholderAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const input = (
    <StyledLink
      to={`${ROUTES.searchAi}?${AI_SEARCH_AUTOFOCUS_PARAM}=true`}
      aria-label={formatMessage({
        id: 'JOBS_SEARCH_AI_ENTRYPOINT_BANNER',
      })}
    >
      <Input
        icon={isSmallScreen ? undefined : IconAIFilled}
        size="xlarge"
        variant="plain"
        value=""
        ref={inputRef}
        aria-hidden="true"
        tabIndex={-1}
      />
    </StyledLink>
  );
  const promotions = <Promotions />;

  if (tipBoxState === 'hidden') {
    return (
      <LightMode>
        <Container>{input}</Container>
        {withPromotions ? promotions : null}
      </LightMode>
    );
  }

  const tipBoxProps: Omit<FindJobsTipBoxProps, 'onClose'> =
    tipBoxState === 'input'
      ? {
          show: true,
          variant: 'input',
          onClickNext: onClickNext('input'),
        }
      : {
          show: isStartPage ? false : isSmallScreen,
          variant: 'promotions',
          onClickNext: onClickNext('promotions'),
          tipBoxProps: { sideOffset: 64 },
        };

  return (
    <LightMode>
      <Container>
        <FindJobsTipBox {...tipBoxProps} onClose={onClose}>
          {input}
        </FindJobsTipBox>
      </Container>
      {withPromotions ? (
        tipBoxState === 'promotions' ? (
          <Promotions
            onClickNext={onClickNext('promotions')}
            onClose={onClose}
            tipbox
          />
        ) : (
          promotions
        )
      ) : null}
    </LightMode>
  );
};

const StyledLink = styled(Link)`
  display: block;
  margin: 0 auto;
  padding: 0 ${spaceL};
  max-width: 648px;

  &:hover,
  &:focus {
    text-decoration: none;
  }

  @media ${mediaConfined} {
    padding: 0;
  }
`;

// Required in order to target the TipBox
const Container = styled.div`
  & button[aria-haspopup='dialog'] {
    display: block;
    margin: 0 auto;
    max-width: 648px;
    width: 100%;

    @media ${mediaConfined} {
      padding: 0;
    }
  }

  & [data-xds='TipBox'] {
    max-width: 220px;
  }
`;

const Input = styled(InputBar)`
  & input {
    cursor: pointer;
    transition: border 0.18s ease-in-out;

    &::placeholder {
      font-size: ${scale080};
    }

    @media ${mediaConfined} {
      height: 90px;
      border-radius: 45px;

      &::placeholder {
        font-size: ${scale090};
      }
    }
  }
`;
